import type { FC, ReactNode } from 'react'

import { Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme } from '@/lib/theme'
import { useI18n } from '@/lib/i18n/context'
import { defineText, useText } from '@/lib/i18n/text'
import { Segmented } from '@/components/dashboard/segmented'

const TEXT = defineText({
    language: 'Idioma',
    dark: 'Modo oscuro',
    light: 'Modo claro',
    toDark: 'Cambiar a modo oscuro',
    toLight: 'Cambiar a modo claro'
}, {
    language: 'Language',
    dark: 'Dark mode',
    light: 'Light mode',
    toDark: 'Switch to dark mode',
    toLight: 'Switch to light mode'
})

interface ThemeToggleProps {
    withLabel?: boolean
    className?: string
}

export const ThemeToggle: FC<ThemeToggleProps> = ({ withLabel = false, className }): ReactNode => {
    const { theme, toggleTheme } = useTheme()
    const text = useText(TEXT)
    const dark = theme === 'dark'
    const Icon = dark ? Sun : Moon

    return (
        <button
            type='button'
            onClick={toggleTheme}
            aria-label={dark ? text.toLight : text.toDark}
            title={dark ? text.toLight : text.toDark}
            className={cn(
                'inline-flex h-8 items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-xs font-medium text-foreground transition-colors outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring',
                !withLabel && 'w-8 justify-center px-0',
                className
            )}
        >
            <Icon className='size-4' aria-hidden='true' />
            {withLabel && (dark ? text.light : text.dark)}
        </button>
    )
}

export const LanguageToggle: FC<{ className?: string }> = ({ className }): ReactNode => {
    const { locale, setLocale } = useI18n()
    const text = useText(TEXT)

    return (
        <Segmented
            className={className}
            label={text.language}
            value={locale}
            onChange={setLocale}
            options={[
                { value: 'es-mx', label: 'ES' },
                { value: 'en', label: 'EN' }
            ]}
        />
    )
}
